import { format } from 'date-fns'
import type {
	DateSelection,
	RangeDateSelection,
	SingleDateSelection,
	TimeValue
} from '@repo/commons/models'

export type DatePickerMode = 'single' | 'range'
export type TimeFormat = '12h' | '24h'

export const DEFAULT_TIME_VALUE: TimeValue = {
	hours: '12',
	minutes: '00',
	period: 'PM'
}

export const DEFAULT_24H_TIME_VALUE: TimeValue = {
	hours: '12',
	minutes: '00',
	period: '' // Not used in 24h format
}

export const DEFAULT_DATE_FORMAT = 'dd/MM/yyyy'
export const DEFAULT_TIME_FORMAT: TimeFormat = '12h'

export function formatDateValue(
	value: DateSelection,
	mode: DatePickerMode,
	dateFormat: string,
	enableTimeSelection: boolean,
	timeFormat: TimeFormat = DEFAULT_TIME_FORMAT
): string {
	if (!value) return ''

	if (mode === 'single') {
		const selection = value as SingleDateSelection
		if (!selection.date) return ''

		let result = format(selection.date, dateFormat)

		if (enableTimeSelection && selection.time) {
			result += ` ${selection.time.hours}:${selection.time.minutes}`
			if (timeFormat === '12h') {
				result += ` ${selection.time.period}`
			}
		}

		return result
	}

	const range = value as RangeDateSelection
	if (!range?.from) return ''

	let fromStr = format(range.from, dateFormat)
	if (enableTimeSelection && range.fromTime) {
		fromStr += ` ${range.fromTime.hours}:${range.fromTime.minutes}`
		if (timeFormat === '12h') {
			fromStr += ` ${range.fromTime.period}`
		}
	}

	if (!range.to) return fromStr

	let toStr = format(range.to, dateFormat)
	if (enableTimeSelection && range.toTime) {
		toStr += ` ${range.toTime.hours}:${range.toTime.minutes}`
		if (timeFormat === '12h') {
			toStr += ` ${range.toTime.period}`
		}
	}

	return `${fromStr} – ${toStr}`
}

export function getCalendarValue(value: DateSelection, mode: DatePickerMode) {
	if (!value) return undefined

	if (mode === 'single') {
		const selection = value as SingleDateSelection
		return selection.date
	} else {
		const range = value as RangeDateSelection
		return {
			from: range.from,
			to: range.to
		}
	}
}

export function getInitialMonth(value: DateSelection, mode: DatePickerMode): Date {
	if (!value) return new Date()

	if (mode === 'single') {
		return (value as SingleDateSelection).date
	} else {
		return (value as RangeDateSelection).from
	}
}

export function getDefaultHelperText(
	helperText: string | undefined,
	enableTimeSelection: boolean,
	dateFormat: string,
	timeFormat: TimeFormat = DEFAULT_TIME_FORMAT
): string {
	if (helperText) {
		return helperText
	}

	const timeFormatStr = timeFormat === '24h' ? 'HH:mm' : 'hh:mm AA'
	return enableTimeSelection ? `(${dateFormat}, ${timeFormatStr})` : `(${dateFormat})`
}

export function getDefaultPlaceholder(
	placeholder: string | undefined,
	mode: DatePickerMode,
	enableTimeSelection: boolean
): string {
	if (placeholder) {
		return placeholder
	}
	return mode === 'single'
		? enableTimeSelection
			? 'Select date and time'
			: 'Select date'
		: enableTimeSelection
			? 'Select date and time range'
			: 'Select date range'
}

export function createSingleDateSelection(date: Date, time?: TimeValue): SingleDateSelection {
	return {
		date,
		time: time ? { ...time } : undefined
	}
}

export function createRangeDateSelection(
	from: Date,
	to?: Date,
	fromTime?: TimeValue,
	toTime?: TimeValue
): RangeDateSelection {
	return {
		from,
		to,
		fromTime: fromTime ? { ...fromTime } : undefined,
		toTime: to && toTime ? { ...toTime } : undefined
	}
}

// Utility to convert between time formats
export function convertTimeValue(timeValue: TimeValue, to: TimeFormat): TimeValue {
	if (!timeValue) return { ...DEFAULT_TIME_VALUE }

	const result = { ...timeValue }

	if (to === '24h') {
		// Convert from 12h to 24h
		const hours = parseInt(timeValue.hours, 10)
		if (timeValue.period === 'PM' && hours < 12) {
			result.hours = String(hours + 12).padStart(2, '0')
		} else if (timeValue.period === 'AM' && hours === 12) {
			result.hours = '00'
		} else {
			result.hours = String(hours).padStart(2, '0')
		}
	} else {
		// Convert from 24h to 12h
		const hours = parseInt(timeValue.hours, 10)
		if (hours === 0) {
			result.hours = '12'
			result.period = 'AM'
		} else if (hours === 12) {
			result.hours = '12'
			result.period = 'PM'
		} else if (hours > 12) {
			result.hours = String(hours - 12).padStart(2, '0')
			result.period = 'PM'
		} else {
			result.hours = String(hours).padStart(2, '0')
			result.period = 'AM'
		}
	}

	return result
}

// Function to initialize time values based on the format
export function initializeTimeValue(timeFormat: TimeFormat): TimeValue {
	if (timeFormat === '24h') {
		return { ...DEFAULT_24H_TIME_VALUE }
	}

	return { ...DEFAULT_TIME_VALUE }
}
